import { Injectable } from '@angular/core';
import { FormsApiModel, AddFormApiModel, FormsExtendedDataApiModel, ApplicationDTO, QuestionGroup, Survey } from './forms.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';

@Injectable({
    providedIn: 'root'
})
export class FormsService {
    public dataObj: FormsSharedData = {
        forms: []
    };

    public emptySurvey: Survey = <Survey>{
        ApplicationID: 0,
        CompanyID: 0,
        StartDate: new Date(new Date().toDateString()),
        EndDate: null,
        IntervalType: 0,
        IsOpen: false,
        TakeLimitPerTimeFrame: 0
    };

    constructor(
        private cvApiService: CVApiService
    ) { }

    public async loadForms() {
        try {
            let response = await this.cvApiService.get<FormsApiModel>(`/api/v1/forms`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load forms.');
        }
    }

    public async loadFormExtendedData(applicationId: number) {
        try {
            let response = await this.cvApiService.get<FormsExtendedDataApiModel>(`/api/v1/formExtendedData?applicationId=${applicationId}`);
            if (response.status_code >= 200 && response.status_code < 300) {
                response.data.Survey = this.convertSurveyInfo(response.data.Survey);
                return response.data;
            }
            else throw response;
        }
        catch (error) {
            console.error('Failed to load questions.');
        }
    }

    public async loadGlobalGroups() {
        try {
            let response = await this.cvApiService.get<QuestionGroup[]>(`/api/v1/globalGroups`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load global groups.');
        }
    }

    public async addForm(form: ApplicationDTO) {
        try {
            let response = await this.cvApiService.post<AddFormApiModel>(`/api/v1/form?utcOffset=${new Date().getTimezoneOffset()}`, form);
            if (response.status_code >= 200 && response.status_code < 300) {
                response.data.Survey = this.convertSurveyInfo(response.data.Survey);
                return response.data;
            }
            else throw response;
        }
        catch (error) {
            console.error('Failed to save new form.');
        }
    }

    public async editForm(form: ApplicationDTO) {
        try {
            let response = await this.cvApiService.put(`/api/v1/form`, form);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to edit form.');
        }
    }

    public async copyForm(form: ApplicationDTO) {
        try {
            let response = await this.cvApiService.post<ApplicationDTO>(`/api/v1/formCopy`, form);
            if (response.status_code >= 200 && response.status_code < 300) {
                response.data.Survey = this.convertSurveyInfo(response.data.Survey);
                return response.data;
            }
            else throw response;
        }
        catch (error) {
            console.error('Failed to copy form.');
        }
    }

    public async editFormExtendedData(form: ApplicationDTO) {
        try {
            let response = await this.cvApiService.put<ApplicationDTO>(`/api/v1/formExtendedData?utcOffset=${new Date().getTimezoneOffset()}`, form);
            if (response.status_code >= 200 && response.status_code < 300) {
                response.data.Survey = this.convertSurveyInfo(response.data.Survey);
                return response.data;
            }
            else throw response;
        }
        catch (error) {
            console.error('Failed to edit form extended data.');
        }
    }

    public async deleteForms(forms: ApplicationDTO[]) {
        try {
            let response = await this.cvApiService.put<ApplicationDTO>(`/api/v1/forms`, forms);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to delete forms.');
        }
    }

    public async sendSurveyNotification(applicationId: number) {
        try {
            let response = await this.cvApiService.put<ApplicationDTO>(`/api/v1/forms/surveyNotification?applicationId=${applicationId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to send survey notification(s).');
        }
    }

    /**
     * Done to ensure that survey date types are always the proper Survey class from forms.model.ts
     * @param survey Survey to convert
     */
    private convertSurveyInfo(survey) {
        if (!survey)
            return angular.copy(this.emptySurvey);
        else {
            let convertedSurvey = new Survey();
            convertedSurvey.ApplicationID = survey.ApplicationID;
            convertedSurvey.CompanyID = survey.CompanyID;
            convertedSurvey.StartDate = new Date(survey.StartDate);
            convertedSurvey.EndDate = survey.EndDate == null ? null : new Date(survey.EndDate);
            convertedSurvey.IntervalType = survey.IntervalType;
            convertedSurvey.IsOpen = survey.IsOpen;
            convertedSurvey.TakeLimitPerTimeFrame = survey.TakeLimitPerTimeFrame;

            return convertedSurvey;
        }
    }
}

export interface FormsSharedData {
    forms: ApplicationDTO[]
}
